import React from 'react'
import ReactDOM from 'react-dom/client'
import {createBrowserRouter, Navigate, RouterProvider,} from "react-router-dom";

import "./colours.css"
import "./main.css"
import {ErrorProvider} from "./utils/apiErrors.js";
import Home from "./pages/Home/Home.js";
import Login from "./pages/Login/Login.js";
import Logout from "./pages/Logout/Logout.js";
import OnboardingSteps from "./pages/OnboardingSteps/OnboardingSteps.js";
import DatabaseCreate from "./pages/DatabaseCreate/DatabaseCreate.js";
import DatabaseList from "./pages/DatabaseList/DatabaseList.js";
import DatabaseDetails from "./pages/DatabaseDetails/DatabaseDetails.js";
import FilterCreate from "./pages/FilterCreate/FilterCreate.js";
import FilterEdit from "./pages/FilterEdit/FilterEdit.js";
import APIError from "./pages/APIError/APIError.js";

import Debug from "./pages/Debug/Debug.js";
import {GoogleOAuthProvider} from "@react-oauth/google";
import {getGoogleClientId} from "./utils/googleClientId.js";
import DatabaseSchemaChanges from "./pages/DatabaseSchemaChanges/DatabaseSchemaChanges.js";
import DatabaseEdit from "./pages/DatabaseEdit/DatabaseEdit.js";
import initSentry from "./utils/sentry.js";
import TermsOfService from "./pages/TermsOfService/TermsOfService.js";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy.js";
import ReviewTermsOfService from "./pages/ReviewTermsOfService/ReviewTermsOfService.js";
import LoginDebug from "./pages/LoginDebug/LoginDebug.js";
import SyntheticUsersList from "./pages/SyntheticUsersList/SyntheticUsersList.js";
import SyntheticUserCreate from "./pages/SyntheticUserCreate/SyntheticUserCreate.js";
import RolesList from "./pages/RolesList/RolesList.js";
import RoleEdit from "./pages/RoleEdit/RoleEdit.js";
import SyntheticUserEdit from "./pages/SyntheticUserEdit/SyntheticUserEdit.js";
import UserList from "./pages/UserList/UserList.js";
import UserCreate from "./pages/UserCreate/UserCreate.js";
import {getExpired, setExpired} from "./utils/contexts.js";
import SyntheticUserEditPassword from "./pages/SyntheticUserEditPassword/SyntheticUserEditPassword.js";
import UserEdit from "./pages/UserEdit/UserEdit.js";
import DatabaseCSV from "./pages/DatabaseCSV/DatabaseCSV.js";
import RoleCreate from "./pages/RoleCreate/RoleCreate.js";
import Admin from "./pages/Admin/Admin.js";
import DatabaseConfigDebug from "./pages/DatabaseConfigDebug/DatabaseCSV.js";
import Payment from './components/PricingPlans/PricingPlans.js';
import PricingPlans from "./components/PricingPlans/PricingPlans.js";

const isDebug = import.meta.env.VITE_DEBUG === "true"

function ExpiredRedirect({children, check_tos=true} : {children: React.ReactNode, check_tos?: boolean}) {

    const profile = localStorage.getItem("profile")

    // logout if session expired
    if (getExpired() === true) {
        console.log("Session expired")
        setExpired(false)
        return <Navigate to="/logout"/>
    }

    // /login if not logged in
    const profileJson = profile ? JSON.parse(profile) : null
    if (profileJson === null) {
        return <Navigate to="/login"/>
    }
    if (check_tos && profileJson.hasAcceptedTos === false) {
        return <Navigate to="/review_tos"/>
    }

    return <React.Fragment>{children}</React.Fragment>
}

const routes = [
    {
        path: "/",
        element: <ExpiredRedirect><Home/></ExpiredRedirect>,
    }, {
        path: "/login",
        element: <Login/>,
    }, {
        path: "/logout",
        element: <Logout/>,
    }, {
        path: "/onboarding",
        element: <ExpiredRedirect><OnboardingSteps/></ExpiredRedirect>,
    }, {
        path: "/databases",
        element: <ExpiredRedirect><DatabaseList/></ExpiredRedirect>,
    }, {
        path: "/database/create",
        element: <ExpiredRedirect><DatabaseCreate
            returnTo={undefined}
            databaseId={null}
            defaultName={undefined}
            defaultDescription={undefined}
            defaultSchemaOption="Hide Column"
            defaultPageTitle="Create Database"
        /></ExpiredRedirect>,
    }, {
        path: "/database/:id",
        element: <ExpiredRedirect><DatabaseDetails/></ExpiredRedirect>,
    }, {
        path: "/database/:id/csv",
        element: <ExpiredRedirect><DatabaseCSV/></ExpiredRedirect>,
    }, {
        path: "/database/:id/config",
        element: <ExpiredRedirect><DatabaseConfigDebug/></ExpiredRedirect>,
    }, {
        path: "/database/:id/general",
        element: <ExpiredRedirect><DatabaseEdit/></ExpiredRedirect>,
    }, {
        path: "/database/:id/schema_changes",
        element: <ExpiredRedirect><DatabaseSchemaChanges/></ExpiredRedirect>,
    }, {
        path: "/database/:id/filter/create",
        element: <ExpiredRedirect><FilterCreate
            returnTo={undefined}
            edit_id={undefined}
            defaultDatabases={undefined}
            defaultName={undefined}
            defaultDescription={undefined}
            defaultSelectedDatabase={undefined}
            defaultSelectedTable={undefined}
            defaultFilterType={undefined}
            defaultConditions={undefined}
            defaultActions={undefined}
            defaultUsers={undefined}
            defaultSelectedRoles={undefined}
        /></ExpiredRedirect>,
    }, {
        path: "/database/:id/filter/:fid",
        element: <ExpiredRedirect><FilterEdit/></ExpiredRedirect>,
    },{
        path: "/error/api",
        element: <APIError/>,
    },{
        path: "/debug",
        element: <Debug/>,
    },{
        path: "/tos",
        element: <TermsOfService/>,
    },{
        path: "/review_tos",
        element: <ExpiredRedirect check_tos={false}><ReviewTermsOfService/></ExpiredRedirect>,
    },{
        path: "/privacy",
        element: <PrivacyPolicy/>,
    },{
        path: "/admin",
        element: <ExpiredRedirect><Admin/></ExpiredRedirect>,
    }, {
        path: "/user/create",
        element: <ExpiredRedirect><SyntheticUserCreate/></ExpiredRedirect>,
    }, {
        path: "/users",
        element: <ExpiredRedirect><SyntheticUsersList/></ExpiredRedirect>,
    }, {
        path: "/user/:id",
        element: <ExpiredRedirect><SyntheticUserEdit/></ExpiredRedirect>,
    }, {
        path: "/user/:id/password",
        element: <ExpiredRedirect><SyntheticUserEditPassword/></ExpiredRedirect>,
    }, {
        path: "/roles",
        element: <ExpiredRedirect><RolesList/></ExpiredRedirect>,
    }, {
        path: "/role/create",
        element: <ExpiredRedirect><RoleCreate/></ExpiredRedirect>,
    }, {
        path: "/role/:id",
        element: <ExpiredRedirect><RoleEdit/></ExpiredRedirect>,
    }, {
        path: "/organization_users",
        element: <ExpiredRedirect><UserList/></ExpiredRedirect>
    }, {
        path: "/organization_user/create",
        element: <ExpiredRedirect><UserCreate/></ExpiredRedirect>
    }, {
        path: "/organization_user/:id",
        element: <ExpiredRedirect><UserEdit/></ExpiredRedirect>
    }, {
        path: "/payment",
        element: <ExpiredRedirect><PricingPlans /></ExpiredRedirect>
    }
]
if (isDebug) {
    routes.push({
      path: "/login/debug",
      element: <LoginDebug/>,
    })
}
const router = createBrowserRouter(routes);

initSentry()
ReactDOM.createRoot(document.getElementById('root')).render(
    <ErrorProvider>
        <GoogleOAuthProvider clientId={getGoogleClientId()} >
            <RouterProvider router={router} />
        </GoogleOAuthProvider>
    </ErrorProvider>
)
